/**
 * アプリケーションキャッシュユーティリティ
 * アプリケーション内でのデータキャッシュを管理する
 */
// src/utils/app-cache.js

/**
 * アプリケーションキャッシュクラス
 * メモリ内でのデータキャッシュを効率的に管理
 */
class AppCache {
  constructor(options = {}) {
    this.cache = new Map();
    this.maxSize = options.maxSize || 100; // 最大キャッシュサイズ
    this.defaultTTL = options.defaultTTL || 300000; // デフォルトTTL（5分）
    this.cleanupInterval = options.cleanupInterval || 60000; // クリーンアップ間隔（1分）
    this.stats = {
      hits: 0, // キャッシュヒット数
      misses: 0, // キャッシュミス数
      sets: 0, // セット数
      deletes: 0, // 削除数
      evictions: 0 // 追い出し数
    };
    
    // 定期的なクリーンアップを開始
    this.startCleanup();
  }

  /**
   * データをキャッシュに保存
   * @param {string} key - キャッシュキー
   * @param {any} value - 保存するデータ
   * @param {number} ttl - 有効期限（ミリ秒）
   * @returns {boolean} 保存成功の場合true
   */
  set(key, value, ttl = this.defaultTTL) {
    try {
      // キャッシュサイズ制限チェック
      if (this.cache.size >= this.maxSize) {
        this.evictOldest();
      }

      const item = {
        value: this.deepClone(value), // 深いコピーで保存
        timestamp: Date.now(),
        ttl: ttl,
        expires: Date.now() + ttl,
        accessCount: 0 // アクセス回数
      };

      this.cache.set(key, item);
      this.stats.sets++;
      
      console.log(`データがキャッシュされました: ${key} (TTL: ${ttl}ms)`);
      return true;
    } catch (error) {
      console.error(`キャッシュ保存エラー: ${key}`, error);
      return false;
    }
  }

  /**
   * キャッシュからデータを取得
   * @param {string} key - キャッシュキー
   * @returns {any|null} キャッシュされたデータまたはnull
   */
  get(key) {
    try {
      const item = this.cache.get(key);
      
      if (!item) {
        this.stats.misses++;
        return null;
      }

      // 有効期限チェック
      if (Date.now() > item.expires) {
        this.cache.delete(key);
        this.stats.misses++;
        console.log(`キャッシュが期限切れです: ${key}`);
        return null;
      }

      // アクセス回数を増加
      item.accessCount++;
      this.stats.hits++;
      
      console.log(`キャッシュから取得しました: ${key} (アクセス回数: ${item.accessCount})`);
      return this.deepClone(item.value); // 深いコピーで返す
    } catch (error) {
      console.error(`キャッシュ取得エラー: ${key}`, error);
      this.stats.misses++;
      return null;
    }
  }

  /**
   * キャッシュからデータを削除
   * @param {string} key - キャッシュキー
   * @returns {boolean} 削除成功の場合true
   */
  delete(key) {
    try {
      const deleted = this.cache.delete(key);
      if (deleted) {
        this.stats.deletes++;
        console.log(`キャッシュが削除されました: ${key}`);
      }
      return deleted;
    } catch (error) {
      console.error(`キャッシュ削除エラー: ${key}`, error);
      return false;
    }
  }

  /**
   * キャッシュをクリア
   * @returns {boolean} クリア成功の場合true
   */
  clear() {
    try {
      this.cache.clear();
      this.stats = {
        hits: 0,
        misses: 0,
        sets: 0,
        deletes: 0,
        evictions: 0
      };
      console.log('すべてのキャッシュがクリアされました');
      return true;
    } catch (error) {
      console.error('キャッシュクリアエラー:', error);
      return false;
    }
  }

  /**
   * キャッシュにデータが存在するかチェック
   * @param {string} key - キャッシュキー
   * @returns {boolean} 存在する場合true
   */
  has(key) {
    const item = this.cache.get(key);
    if (!item) return false;
    
    // 有効期限チェック
    if (Date.now() > item.expires) {
      this.cache.delete(key);
      return false;
    }
    
    return true;
  }

  /**
   * キャッシュの有効期限を更新
   * @param {string} key - キャッシュキー
   * @param {number} ttl - 新しい有効期限（ミリ秒）
   * @returns {boolean} 更新成功の場合true
   */
  extend(key, ttl) {
    try {
      const item = this.cache.get(key);
      if (!item) return false;
      
      item.ttl = ttl;
      item.expires = Date.now() + ttl;
      
      console.log(`キャッシュの有効期限が更新されました: ${key} (新しいTTL: ${ttl}ms)`);
      return true;
    } catch (error) {
      console.error(`キャッシュ有効期限更新エラー: ${key}`, error);
      return false;
    }
  }

  /**
   * 古いキャッシュを削除（LRU方式）
   */
  evictOldest() {
    let oldestKey = null;
    let oldestTime = Date.now();
    let leastAccessed = Infinity;

    // 最も古く、最もアクセスされていないアイテムを探す
    for (const [key, item] of this.cache.entries()) {
      const score = item.timestamp + (item.accessCount * 1000); // アクセス回数を考慮
      if (score < oldestTime + (leastAccessed * 1000)) {
        oldestTime = item.timestamp;
        leastAccessed = item.accessCount;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
      this.stats.evictions++;
      console.log(`古いキャッシュが削除されました: ${oldestKey}`);
    }
  }

  /**
   * 期限切れのキャッシュをクリーンアップ
   */
  cleanup() {
    const now = Date.now();
    const keysToDelete = [];

    for (const [key, item] of this.cache.entries()) {
      if (now > item.expires) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => this.cache.delete(key));
    
    if (keysToDelete.length > 0) {
      console.log(`${keysToDelete.length}個の期限切れキャッシュが削除されました`);
    }
  }

  /**
   * 定期的なクリーンアップを開始
   */
  startCleanup() {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }
    
    this.cleanupTimer = setInterval(() => {
      this.cleanup();
    }, this.cleanupInterval);
  }

  /**
   * 定期的なクリーンアップを停止
   */
  stopCleanup() {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  /**
   * キャッシュ統計を取得
   * @returns {Object} キャッシュ統計
   */
  getStats() {
    const now = Date.now();
    let expiredCount = 0;
    let totalSize = 0;
    let totalAccessCount = 0;

    for (const item of this.cache.values()) {
      if (now > item.expires) {
        expiredCount++;
      }
      totalSize += JSON.stringify(item.value).length;
      totalAccessCount += item.accessCount;
    }

    const hitRate = this.stats.hits + this.stats.misses > 0 
      ? (this.stats.hits / (this.stats.hits + this.stats.misses) * 100).toFixed(2)
      : 0;

    return {
      totalItems: this.cache.size,
      expiredItems: expiredCount,
      totalSize: totalSize,
      maxSize: this.maxSize,
      hitRate: `${hitRate}%`,
      totalAccessCount: totalAccessCount,
      ...this.stats
    };
  }

  /**
   * キャッシュの詳細情報を取得
   * @returns {Array} キャッシュアイテムの詳細リスト
   */
  getDetails() {
    const now = Date.now();
    const details = [];

    for (const [key, item] of this.cache.entries()) {
      details.push({
        key,
        size: JSON.stringify(item.value).length,
        age: now - item.timestamp,
        ttl: item.ttl,
        expires: item.expires,
        remaining: Math.max(0, item.expires - now),
        accessCount: item.accessCount,
        isExpired: now > item.expires
      });
    }

    return details.sort((a, b) => b.accessCount - a.accessCount); // アクセス回数順でソート
  }

  /**
   * 深いコピーを作成
   * @param {any} obj - コピーするオブジェクト
   * @returns {any} コピーされたオブジェクト
   */
  deepClone(obj) {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }
    
    if (obj instanceof Date) {
      return new Date(obj.getTime());
    }
    
    if (obj instanceof Array) {
      return obj.map(item => this.deepClone(item));
    }
    
    if (typeof obj === 'object') {
      const cloned = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          cloned[key] = this.deepClone(obj[key]);
        }
      }
      return cloned;
    }
    
    return obj;
  }

  /**
   * キャッシュをJSON形式でエクスポート
   * @returns {string} JSON文字列
   */
  export() {
    const data = {
      items: Array.from(this.cache.entries()),
      stats: this.stats,
      timestamp: Date.now()
    };
    return JSON.stringify(data, null, 2);
  }

  /**
   * JSON形式からキャッシュをインポート
   * @param {string} json - JSON文字列
   * @returns {boolean} インポート成功の場合true
   */
  import(json) {
    try {
      const data = JSON.parse(json);
      
      // 既存のキャッシュをクリア
      this.clear();
      
      // データをインポート
      if (data.items) {
        for (const [key, item] of data.items) {
          this.cache.set(key, item);
        }
      }
      
      if (data.stats) {
        this.stats = { ...this.stats, ...data.stats };
      }
      
      console.log('キャッシュがインポートされました');
      return true;
    } catch (error) {
      console.error('キャッシュインポートエラー:', error);
      return false;
    }
  }
}

// デフォルトインスタンスを作成
const appCache = new AppCache({
  maxSize: 100,
  defaultTTL: 300000, // 5分
  cleanupInterval: 60000 // 1分
});

// グローバルに公開（デバッグ用）
if (process.env.NODE_ENV === 'development') {
  window.appCache = appCache;
}

export { AppCache };
export default appCache;



// WEBPACK FOOTER //
// ./src/utils/app-cache.js